import React from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useSelector } from 'react-redux';
import { isUndefined } from 'utils/commonUtils';
import { BrowserRouter } from 'react-router-dom';
import { StoreState } from 'types';

import Custom403 from 'pages/errors/403';
import Custom500 from 'pages/errors/500';
import Custom500C from 'pages/errors/500_cashdesk';
import Fallback from 'components/fallback/fallback';
import Login from 'pages/login/login';

const Dashboard = React.lazy(() => import('pages/secured/dashboard/dashboard'));
const Cashdesk = React.lazy(() => import('pages/secured/cashdesk/cashdesk'));
const CashdeskHistory = React.lazy(() => import('pages/secured/cashdesk/history'));
const Calendar = React.lazy(() => import('pages/secured/calendar/calendar'));
const Reports = React.lazy(() => import('pages/secured/report/report'));
const Terms = React.lazy(() => import('pages/secured/term/calendar'));
const CompanySetting = React.lazy(() => import('pages/secured/setting/setting'));
const AccountSetting = React.lazy(() => import('pages/secured/setting/account'));
const BranchDetail = React.lazy(() => import('pages/secured/branch/detail'));
const ProductDetail = React.lazy(() => import('pages/secured/product/detail'));
const ServiceDetail = React.lazy(() => import('pages/secured/service/detail'));
const EmployeeDetail = React.lazy(() => import('pages/secured/employee/detail'));
const CustomersTable = React.lazy(() => import('pages/secured/customer/table'));
const Operations = React.lazy(() => import('pages/secured/operations/operations'));
const CustomerDetail = React.lazy(() => import('pages/secured/customer/detail'));

const App = () => {
    const { token, isBasicEmployee, isOwner } = useSelector((state: StoreState) => state.login);
    const { strictCashdesk, appSetting } = useSelector((state: StoreState) => state.global);

    const getLoginRoutes = () => {
        return (
            <>
                <Route element={<Login />} path="/" />
                <Route path="*" element={<Navigate replace to="/" />} />
            </>
        );
    };

    const getBasicRoutes = () => {
        return (
            <>
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <AccountSetting />
                        </React.Suspense>
                    }
                    path="/secured/setting/my-account"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Reports />
                        </React.Suspense>
                    }
                    path="/secured/reports"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Calendar />
                        </React.Suspense>
                    }
                    path="/secured/calendar"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Terms />
                        </React.Suspense>
                    }
                    path="/secured/terms"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <CustomersTable />
                        </React.Suspense>
                    }
                    path="/secured/customers"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <CustomerDetail />
                        </React.Suspense>
                    }
                    path="/secured/customer/:id"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Custom403 />
                        </React.Suspense>
                    }
                    path="/secured/403"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Custom500 />
                        </React.Suspense>
                    }
                    path="/secured/500"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Dashboard />
                        </React.Suspense>
                    }
                    path="/secured/dashboard"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Cashdesk />
                        </React.Suspense>
                    }
                    path="/secured/cashdesk"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <CashdeskHistory />
                        </React.Suspense>
                    }
                    path="/secured/history"
                />
                <Route path="*" element={<Navigate replace to="/secured/dashboard" />} />
            </>
        );
    };

    const getManagerRoutes = () => {
        return (
            <>
                {getBasicRoutes()}
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Operations />
                        </React.Suspense>
                    }
                    path="/secured/operations"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <ServiceDetail />
                        </React.Suspense>
                    }
                    path="/secured/service/:id"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <EmployeeDetail />
                        </React.Suspense>
                    }
                    path="/secured/employee/:id"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <ProductDetail />
                        </React.Suspense>
                    }
                    path="/secured/product/:id"
                />
            </>
        );
    };

    const getOwnerRoutes = () => {
        return (
            <>
                {getManagerRoutes()}
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <BranchDetail />
                        </React.Suspense>
                    }
                    path="/secured/branch/:id"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <CompanySetting />
                        </React.Suspense>
                    }
                    path="/secured/setting/company"
                />
            </>
        );
    };

    const getCashdeskRoutes = () => {
        return (
            <>
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Cashdesk />
                        </React.Suspense>
                    }
                    path="/secured/cashdesk"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <CashdeskHistory />
                        </React.Suspense>
                    }
                    path="/secured/history"
                />
                <Route
                    element={
                        <React.Suspense fallback={<Fallback loading />}>
                            <Custom500C />
                        </React.Suspense>
                    }
                    path="/secured/500"
                />
                <Route path="*" element={<Navigate replace to="/secured/cashdesk" />} />
            </>
        );
    };

    const renderBrowserRouter = (routes: JSX.Element) => {
        return (
            <BrowserRouter>
                <Routes>{routes}</Routes>
            </BrowserRouter>
        );
    };

    // RENDER BASED ON STH
    if (isUndefined(token)) {
        return renderBrowserRouter(getLoginRoutes());
    }

    if (strictCashdesk && appSetting?.enable_cashdesk) {
        return renderBrowserRouter(getCashdeskRoutes());
    }

    if (isOwner) {
        return renderBrowserRouter(getOwnerRoutes());
    }

    if (!isBasicEmployee) {
        return renderBrowserRouter(getManagerRoutes());
    }

    return renderBrowserRouter(getBasicRoutes());
};

export default App;
